/**
 * Cicha Pewność - strona główna jest jedną uporządkowaną ścieżką od niskoprogowego kontaktu, przez dowody i test, do świadomej decyzji o spotkaniu.
 */
import React, { lazy, Suspense, type ComponentType } from "react";
import { ArrowRight, MapPin, Phone, ShieldCheck } from "lucide-react";
import { GoogleRatingBadge } from "@/components/site/GoogleRatingBadge";
import { SiteLayout } from "@/components/site/SiteLayout";
import { InstallmentPaymentHighlight } from "@/components/site/ConversionBlocks";
import { contact } from "@/lib/site-data";

const LazyHomeBelowFold = lazy(() => import("./HomeBelowFold"));
export type HomeSsrSections = { belowFold: ComponentType };

export default function Home({
  ssrSections,
}: {
  ssrSections?: HomeSsrSections;
}) {
  const BelowFold = ssrSections?.belowFold ?? LazyHomeBelowFold;
  return (
    <SiteLayout
      title="Upadłości Ekspert - pomoc w upadłości konsumenckiej"
      description="Upadłości Ekspert pomaga w sprawach o upadłość konsumencką i oddłużenie. Bezpłatnie przeanalizujemy Twoją sytuację, wyjaśnimy możliwe kroki oraz formę kontaktu: telefonicznie, online lub w kancelarii. Prowadzimy sprawy od przygotowania wniosku sądowego do postanowienia sądu, też bez konieczności Twojej wizyty w sądzie."
    >
      <section className="hero">
        <div className="shell-wide hero-grid">
          <div className="hero-copy rise-in">
            <p className="hero-label hero-label--expanded">
              <span /> Częste umorzenia bez ustalenia planu spłaty
            </p>
            <h1>Upadłość konsumencka - skuteczne i legalne oddłużenie</h1>
            <p>Zadzwoń i umów bezpłatną analizę z naszym Ekspertem</p>
            <p className="hero-comprehensive-note">
              Prowadzimy Cię od pierwszej rozmowy do zakończenia postępowania -
              bez konieczności Twojej wizyty w sądzie.
            </p>
            <p className="hero-social-proof">
              <span>✓ 5 789 przeprowadzonych upadłości</span>
              <span>
                ✓ 61 mln zł - największy umorzony dług jednego Klienta
              </span>
              <span>
                ✓ Umorzenie bez ustalania planu spłaty w jednej ze spraw
              </span>
              <span>✓ 26 lat doświadczenia naszego zespołu</span>
            </p>
            <div className="hero-google-rating">
              <GoogleRatingBadge />
            </div>
            <InstallmentPaymentHighlight className="hero-installment-highlight" />
            <div className="hero-actions hero-contact-selector">
              <a
                className="button-primary phone-primary cta-attention cta-attention-phone"
                href={contact.phoneHref}
              >
                <Phone size={18} /> Zadzwoń teraz: {contact.phoneDisplay}
              </a>
              <a
                className="button-secondary meeting-secondary cta-attention cta-attention-meeting"
                href="/#kontakt"
              >
                <MapPin size={18} /> Bezpłatna konsultacja
              </a>
            </div>
            <a href="/#test" className="hero-tertiary-link">
              Nie wiesz, od czego zacząć?{" "}
              <strong>Zrób anonimowy test (2 minuty)</strong>{" "}
              <ArrowRight size={16} />
            </a>
            <p className="hero-microcopy">
              <ShieldCheck size={16} /> Sprawdź anonimowo swoją sytuację i
              wybierz odpowiedni dla Ciebie sposób kontaktu.
            </p>
          </div>
          <figure className="hero-visual hero-man rise-in">
            <img
              src="/manus-storage/hero-wczesniejszy-piotr_2146f400.png"
              width="1316"
              height="1195"
              fetchPriority="high"
              decoding="async"
              alt="Ekspert Upadłości Ekspert podczas rozmowy w kancelarii"
            />
          </figure>
        </div>
      </section>
      <Suspense fallback={null}>
        <BelowFold />
      </Suspense>
    </SiteLayout>
  );
}
